'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["order", "children", "direction", "onOrderChange", "DragList"];
import PropTypes from 'prop-types';
import { Children, Component } from 'react';
import { findDOMNode } from 'react-dom';
import invariant from 'react-utils/invariant';
import { callIfPossible } from 'foundations-components/transitional/utils/functions';
import UIDragList from './UIDragList';
import { jsx as _jsx } from "react/jsx-runtime";
class UISortableList extends Component {
  constructor(_props) {
    super(_props);
    this.updateStateOrder = order => {
      // Does not update the `orderFromProps`
      this.setState({
        orderFromState: order
      });
    };
    this.getHoverMiddleCoordinate = ({
      direction
    }, {
      hoverBoundingRect
    }) => {
      if (direction === 'horizontal') {
        return (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      }
      return (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
    };
    this.getHoverClientCoordinate = ({
      direction
    }, {
      clientOffset,
      hoverBoundingRect
    }) => {
      if (direction === 'horizontal') {
        return clientOffset.x - hoverBoundingRect.left;
      }
      return clientOffset.y - hoverBoundingRect.top;
    };
    this.handleOrderChange = (dragId, hoverId) => {
      const {
        orderFromState
      } = this.state;
      const newOrder = orderFromState.slice(0);
      const dragIndex = newOrder.indexOf(dragId);
      const hoverIndex = newOrder.indexOf(hoverId);
      newOrder.splice(dragIndex, 1);
      const leftSide = newOrder.slice(0, hoverIndex);
      const rightSide = newOrder.slice(hoverIndex);
      this.updateStateOrder([...leftSide, dragId, ...rightSide]);
    };
    this.handleDrop = () => {
      const {
        onOrderChange
      } = this.props;
      const {
        orderFromState
      } = this.state;
      callIfPossible(onOrderChange, orderFromState);
    };
    this.handleHover = (props, monitor, component) => {
      const {
        orderFromState
      } = this.state;
      const {
        direction
      } = props;
      const dragId = monitor.getItem().dragId;
      const hoverId = props.dragId;
      const dragIndex = orderFromState.indexOf(dragId);
      const hoverIndex = orderFromState.indexOf(hoverId);
      if (dragIndex === hoverIndex) {
        return;
      }

      // eslint-disable-next-line react/no-find-dom-node
      const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();
      const hoverMiddleCoordinate = this.getHoverMiddleCoordinate({
        direction
      }, {
        hoverBoundingRect
      });
      const clientOffset = monitor.getClientOffset();
      const hoverClientCoordinate = this.getHoverClientCoordinate({
        direction
      }, {
        clientOffset,
        hoverBoundingRect
      });
      const indexIsBelow = dragIndex < hoverIndex;
      const hoverOnNextHalf = hoverClientCoordinate < hoverMiddleCoordinate;
      if (indexIsBelow && hoverOnNextHalf) {
        return;
      }
      const indexIsAbove = dragIndex > hoverIndex;
      const hoverOnPriorHalf = hoverClientCoordinate > hoverMiddleCoordinate;
      if (indexIsAbove && hoverOnPriorHalf) {
        return;
      }
      this.handleOrderChange(dragId, hoverId);
    };
    const {
      direction: _direction,
      order: _order,
      onOrderChange: _onOrderChange,
      children,
      withDragDropContext,
      DragList
    } = _props;
    this.state = {
      orderFromState: _order,
      // Initializes the state `order` to be the `order` from `props`

      // Holds the prop values in state. These values will be compared to the new prop values in `getDerivedStateFromProps`.
      directionFromProps: _direction,
      orderFromProps: _order,
      onOrderChangeFromProps: _onOrderChange,
      childrenFromProps: children,
      withDragDropContextFromProps: withDragDropContext,
      DragListFromProps: DragList
    };
  }
  static getDerivedStateFromProps({
    direction: newDirectionFromProps,
    order: newOrderFromProps,
    onOrderChange: newOnOrderChangeFromProps,
    children: newChildrenFromProps,
    withDragDropContext: newWithDragDropContextFromProps,
    DragList: NewDragListFromProps
  }, {
    directionFromProps: prevDirectionFromProps,
    orderFromProps: prevOrderFromProps,
    onOrderChangeFromProps: prevOnOrderChangeFromProps,
    childrenFromProps: prevChildrenFromProps,
    withDragDropContextFromProps: prevWithDragDropContextFromProps,
    DragListFromProps: PrevDragListFromProps
  }) {
    // If any of the props changed, update the order state value to be the new order prop value. Also, update the prop values stored in state.
    if (newDirectionFromProps !== prevDirectionFromProps || newOrderFromProps !== prevOrderFromProps || newOnOrderChangeFromProps !== prevOnOrderChangeFromProps || newChildrenFromProps !== prevChildrenFromProps || newWithDragDropContextFromProps !== prevWithDragDropContextFromProps || NewDragListFromProps !== PrevDragListFromProps) {
      return {
        orderFromState: newOrderFromProps,
        // Updates the order state value

        // Updates the prop values stored in state
        directionFromProps: newDirectionFromProps,
        orderFromProps: newOrderFromProps,
        onOrderChangeFromProps: newOnOrderChangeFromProps,
        childrenFromProps: newChildrenFromProps,
        withDragDropContextFromProps: newWithDragDropContextFromProps,
        DragListFromProps: NewDragListFromProps
      };
    }
    return null;
  }
  render() {
    const _this$props = this.props,
      {
        children,
        DragList
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      orderFromState
    } = this.state;
    const arrayOfChildren = Children.toArray(children);
    const orderedChildren = arrayOfChildren.reduce((acc, child) => {
      const index = orderFromState.indexOf(child.props.dragId);
      invariant(index !== -1, 'All ids in `order` must match a child with the value id as `dragId`');
      acc[index] = child;
      return acc;
    }, orderFromState.map(() => null));
    invariant(orderedChildren.indexOf(null) === -1, 'All ids in `order` must match a child with the same value as `dragId`');
    return /*#__PURE__*/_jsx(DragList, Object.assign({
      onDndHover: this.handleHover,
      onDndDrop: this.handleDrop,
      onEndDrag: this.handleDrop
    }, rest, {
      children: orderedChildren
    }));
  }
}
UISortableList.propTypes = {
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  order: PropTypes.arrayOf(PropTypes.string),
  onOrderChange: PropTypes.func,
  children: PropTypes.node,
  withDragDropContext: PropTypes.bool,
  DragList: PropTypes.elementType.isRequired
};
UISortableList.defaultProps = {
  direction: 'vertical',
  DragList: UIDragList
};
export default UISortableList;